// COULD NOT FIND BETTER NAME SO I USED THE NAME OF THE CONTENT RATHER THAN THE FORMAT

import cx from 'classnames'
import React, { forwardRef, useEffect, useId, useState } from 'react'
import { useButton } from 'react-aria'
import { ActionButtonProps } from 'src/components/ActionButton'
import { Icons } from 'src/components/Icon'
import { PictureProps } from 'src/components/Picture'
import { RichTextProps } from 'src/components/RichText'
import { springTransition } from 'src/hooks/useAppear'
import { colors } from 'src/theme'
import { motion } from 'motion/react'

import * as SC from './styled'

export type PerspectiveProps = MinimalSeoBlock & {
  className?: string
  title: string
  richTextProps?: RichTextProps
  actionButtonProps?: ActionButtonProps
  pictureProps?: PictureProps
  color?: string
}

const Perspective = forwardRef<HTMLElement, PerspectiveProps>(
  (
    {
      className,
      htmlTag,
      htmlTitleTag,
      title,
      richTextProps,
      actionButtonProps,
      pictureProps,
      color = colors.blueLight,
    },
    ref
  ) => {
    const id = useId()
    const [open, setOpen] = useState(false)
    const [heightContent, setHeightContent] = useState(250)
    const refButton = React.useRef<HTMLDivElement>(null)
    const refContent = React.useRef<HTMLDivElement>(null)

    const handleToggle = () => {
      setOpen(!open)
    }

    const { buttonProps } = useButton(
      {
        id: `${id}-button`,
        'aria-controls': `${id}-content`,
        onPress: handleToggle,
        'aria-expanded': open,
      },
      refButton
    )

    // get height of the content on load and resize
    useEffect(() => {
      const handleHeight = () => {
        if (refContent.current) {
          setHeightContent(refContent.current.clientHeight)
        }
      }
      // delayed version because inside the slider it was not going great ):
      const handleHeightDelayed = () => {
        setTimeout(handleHeight, 300)
      }
      // on load
      handleHeightDelayed()
      // on resize
      window.addEventListener('resize', handleHeightDelayed)
      return () => window.removeEventListener('resize', handleHeightDelayed)
    }, [])

    // close the switch on "escape"
    useEffect(() => {
      if (open === false) return
      const handlePressEscape = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          setOpen(false)
        }
      }
      window.addEventListener('keydown', handlePressEscape)
      return () => {
        window.removeEventListener('keydown', handlePressEscape)
      }
    }, [open])

    return (
      <SC.Container
        ref={ref}
        className={cx('Perspective', className)}
        as={htmlTag}
        $color={color}
        initial="initial"
        animate="initial"
        whileHover="zoom"
      >
        <SC.Details
          data-open={open}
          animate={
            open
              ? {
                  backgroundColor: 'var(--perspective-color)',
                  x: '0%',
                  transition: springTransition,
                }
              : {
                  x: '20%',
                  transition: springTransition,
                }
          }
          variants={{
            initial: {
              transition: springTransition,
            },
            zoom: {
              backgroundColor: 'var(--perspective-color)',
              transition: springTransition,
            },
          }}
        >
          <SC.Summary ref={refButton} {...buttonProps} tabIndex={0}>
            <SC.Title as={htmlTitleTag}>{title}</SC.Title>
            <SC.Toggle
              aria-hidden
              animate={
                open
                  ? { paddingInlineEnd: '0%', transition: springTransition }
                  : { paddingInlineEnd: '20%', transition: springTransition }
              }
            >
              <SC.OpenIcon
                icon={Icons.plus}
                variants={{
                  initial: { rotate: 0, transition: springTransition },
                  zoom: { rotate: 180, transition: springTransition },
                }}
              />
              <SC.CloseIcon icon={Icons.minus} />
            </SC.Toggle>
            <SC.Clickable onClick={handleToggle} aria-hidden={true} />
          </SC.Summary>
          <SC.Wrapper
            // hidden={!open}
            id={`${id}-content`}
            // $height={heightContent}
            aria-labelledby={`${id}-button`}
            animate={
              open
                ? { opacity: 1, height: 'auto', transition: springTransition }
                : { opacity: 0, height: 0, transition: springTransition }
            }
          >
            <SC.Content
              ref={refContent}
              data-height={heightContent}
              animate={
                open
                  ? { opacity: 1, transition: springTransition }
                  : { opacity: 0, transition: springTransition }
              }
            >
              <SC._RichText {...richTextProps} />
              {actionButtonProps && (
                <motion.div
                  animate={
                    open
                      ? {
                          opacity: 1,
                          y: 0,
                          transition: { ...springTransition, delay: 0.12 },
                        }
                      : {
                          opacity: 0,
                          y: 56,
                          transition: springTransition,
                        }
                  }
                >
                  <SC._ActionButton {...actionButtonProps} />
                </motion.div>
              )}
            </SC.Content>
          </SC.Wrapper>
        </SC.Details>
        <SC._Picture
          {...pictureProps}
          variants={{
            initial: { scale: 1.0, transition: springTransition },
            zoom: { scale: 1.03, transition: springTransition },
          }}
        />
      </SC.Container>
    )
  }
)

Perspective.displayName = 'Perspective'

export default Perspective
